/**
 * PORT: the offer to save a battle's replay (the owner's remark: the main screen's replay button saved the same
 * replay again at every press, and it came back each time the player returned to the main screen).
 *
 * A battle whose replay the game keeps (end_game_fn: career, missions, survival, boss; not 'just' or assault) leaves
 * an offer: that battle's legacy string (REPLAY_STRING), mode, stage, result and the backend's battle id. The main
 * screen's replay button (get_replay_clip) is shown while the signed-in player's offer is undecided, and a press opens
 * the game's question popup (menu_question, mode `replay_offer`) instead of saving at once:
 *  - "Accept" saves the replay (replay.save with the battle id; the string is copied to the clipboard, as the
 *    original did): once the server answered, the battle is 'accepted', the window closes and the button goes;
 *  - "Decline" makes the battle 'declined': the window closes and the battle is never offered again;
 *  - the popup's close arrow (Escape, the back button) only closes the window: the offer stays.
 * The offer and the decisions are client preferences of the player (FWP.pref REPLAY_OFFER_PREF, wiped at a sign-out:
 * FWP.PLAYER_PREFS), so they outlive screen changes, a reload and a restart of the app; the decisions of the last
 * DECISIONS_KEPT battles are kept. The server stores one replay per battle id whatever the client sends
 * (ReplayService.save), so a retried or repeated save never stores a second one.
 * The window: the frame, title and text of menu_question, its "Continue" replaced by the two buttons of the game's
 * style (BigButton: green "Accept", beige "Decline") side by side, a finger's size in touch mode (phones, the app); on
 * a wide screen the popup itself is shown larger (ui/wide-popups.ts).
 */
import { MouseEvent } from '@flash';
import { BigButton, shrinkToFit } from '../ui/controls';
import { touchTextsOn } from './i18n';
import { netErrorCode } from './net-policy';

export const MSG_REPLAY_OFFER_TITLE = 'Сохранить повтор боя?';
export const MSG_REPLAY_OFFER_TEXT = 'Запись этого боя сохранится на сервере игры и скопируется в буфер обмена. Её можно будет посмотреть в меню Просмотры боев. Если отказаться, предложение для этого боя больше не появится.';
export const MSG_REPLAY_OFFER_ACCEPT = 'Принять';
export const MSG_REPLAY_OFFER_DECLINE = 'Отклонить';
export const MSG_REPLAY_SAVED = 'Повтор боя сохранён.';

/**
 * The name of a saved replay in the replays tab (its row: new_interf note_name): the mode and stage in the game's own
 * words ("Карьера №3", the catalogue translates them as everywhere else) and when it was played, never the internal
 * ids ("FWP career 3"). Battles of other modes (not offered: 'just', 'assault') get a general name.
 */
export const REPLAY_TITLE_PREFIX: Readonly<Record<string, string>> = {
  career: 'Карьера №',
  missions: 'Миссия №',
  survival: 'Выживание №',
  boss: 'Босс №',
  assault: 'Территория №',
};
export const MSG_REPLAY_TITLE_JUST = 'Просто играть';

export function replayTitle(r: { mode: string; stage: number; createdAt?: number | null }): string {
  const prefix = REPLAY_TITLE_PREFIX[String(r.mode)];
  const name = prefix ? `${prefix}${Number(r.stage) || 1}` : MSG_REPLAY_TITLE_JUST;
  const at = Number(r.createdAt);
  if (!(at > 0)) return name;
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${name} — ${two(d.getDate())}.${two(d.getMonth() + 1)}.${d.getFullYear()}, ${two(d.getHours())}:${two(d.getMinutes())}`;
}
export const MSG_REPLAY_REFUSED = 'Сервер не принял запись этого боя.';
/** The replay button's hint (popup_modify get_replay_clip). */
export const MSG_REPLAY_HINT = 'Нажмите на этот значок, и игра предложит сохранить запись этого боя на сервере игры. Сохранённую запись можно найти в меню Просмотры боев и посмотреть повтор боя.';

/** The question popup's mode of the offer (new_interf.question_mode). */
export const REPLAY_OFFER_MODE = 'replay_offer';
/** The client preference that keeps the offer and the decisions (FWP.pref; one of FWP.PLAYER_PREFS). */
export const REPLAY_OFFER_PREF = 'replay_offer';
/** How many battles' decisions are kept (the newest). */
export const DECISIONS_KEPT = 50;
/** The window's buttons. */
export const ACCEPT_NAME = 'replay_offer_accept';
export const DECLINE_NAME = 'replay_offer_decline';

export type ReplayDecision = 'accepted' | 'declined';

/** The last battle's replay the player may save. */
export interface ReplayOffer {
  /** The player whose battle it was (FWP.user.player_id). */
  player: string;
  /** The battle's key: the backend's battle id, else one of the replay string ('l_' + its hash). */
  key: string;
  battleId: string | null;
  mode: string;
  stage: number;
  win: boolean;
  /** The legacy `!PARAMS!ROUNDS!chk!` string. */
  legacy: string;
}

interface Stored {
  offer: ReplayOffer | null;
  /** [battle key, decision], the newest last. */
  decided: Array<[string, ReplayDecision]>;
}

/** The window's buttons in touch mode: the present window's 1.2 (monetization-ui PRESENT_TOUCH_SCALE). */
const TOUCH_SCALE = 1.2;
/**
 * The buttons' row (menu_question's "Continue": 145 x 40 at y 340), centred between the frame's decorations (the
 * pencils at its bottom left end at x 230, the beanstalk starts at 596), and the room between the two buttons.
 */
const ROW_Y = 340;
const ROW_MID = 413;
const GAP = 12;

/** A short hash of a string (FNV-1a, base 36). */
function hashOf(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

function playerOf(fwp: any): string {
  return fwp && fwp.user && fwp.user.player_id != null ? String(fwp.user.player_id) : '';
}

function load(fwp: any): Stored {
  const raw = fwp.pref(REPLAY_OFFER_PREF);
  const out: Stored = { offer: null, decided: [] };
  if (!raw || typeof raw !== 'object') return out;
  const o = raw.offer;
  if (o && typeof o === 'object' && typeof o.key === 'string' && typeof o.legacy === 'string' && o.legacy !== '') {
    out.offer = { player: String(o.player ?? ''), key: o.key, battleId: typeof o.battleId === 'string' ? o.battleId : null, mode: String(o.mode ?? 'just'), stage: Number(o.stage) || 0, win: !!o.win, legacy: o.legacy };
  }
  if (Array.isArray(raw.decided)) {
    for (const d of raw.decided) if (Array.isArray(d) && typeof d[0] === 'string' && (d[1] === 'accepted' || d[1] === 'declined')) out.decided.push([d[0], d[1]]);
  }
  return out;
}

function store(fwp: any, st: Stored): void {
  fwp.pref(REPLAY_OFFER_PREF, { offer: st.offer, decided: st.decided.slice(-DECISIONS_KEPT) });
}

/** The player's decision on the battle `key`, or null. */
export function replayDecision(fwp: any, key: string): ReplayDecision | null {
  const d = load(fwp).decided.find(([k]) => k === key);
  return d ? d[1] : null;
}

/** The signed-in player's undecided offer, or null. */
export function replayOffer(fwp: any): ReplayOffer | null {
  const st = load(fwp);
  const o = st.offer;
  if (!o || o.player !== playerOf(fwp)) return null;
  return st.decided.some(([k]) => k === o.key) ? null : o;
}

/** Whether the main screen shows the replay button. */
export function replayOfferShown(fwp: any): boolean {
  return replayOffer(fwp) !== null;
}

/**
 * end_game_fn: the battle that just ended leaves its offer (`battle`: null where the game keeps no replay of it, a
 * 'just' or assault battle, as the original's REPLAY_STRING = ""); the offer of an earlier battle goes.
 */
export function offerReplay(fwp: any, battle: { battleId: string | null; mode: string; stage: number; win: boolean; legacy: string } | null): void {
  const st = load(fwp);
  st.offer = battle && battle.legacy ? { player: playerOf(fwp), key: battle.battleId ? battle.battleId : 'l_' + hashOf(battle.legacy), battleId: battle.battleId, mode: battle.mode, stage: battle.stage, win: battle.win, legacy: battle.legacy } : null;
  store(fwp, st);
}

/** net_battle_over: the outcome the server applied to the battle `battleId` is the offer's result. */
export function replayOfferResult(fwp: any, battleId: string, win: boolean): void {
  const st = load(fwp);
  if (!st.offer || st.offer.battleId !== battleId || st.offer.win === win) return;
  st.offer.win = win;
  store(fwp, st);
}

function decide(fwp: any, key: string, decision: ReplayDecision): void {
  const st = load(fwp);
  st.decided = st.decided.filter(([k]) => k !== key);
  st.decided.push([key, decision]);
  store(fwp, st);
}

/** The replay button of the main screen in the game's stack shows the offer's state (it is not drawn again under a popup). */
export function syncReplayButton(fwp: any): void {
  const i = fwp && fwp._interf;
  if (!i || !i.cur_screen) return;
  for (let n = 1; n <= i.cur_screen[0]; n++) {
    const s = i.cur_screen[n];
    if (s && s.name == 'mainframe' && s.link && s.link.get_replay_clip) s.link.get_replay_clip.visible = replayOfferShown(fwp);
  }
}

/** The replay button's press (new_interf.click): the offer's window. */
export function openReplayOffer(interf: any): void {
  if (!replayOfferShown(interf.FWP)) {
    syncReplayButton(interf.FWP);
    return;
  }
  interf.init('question', REPLAY_OFFER_MODE);
}

function isOfferOpen(interf: any): boolean {
  const top = interf.cur_screen[interf.cur_screen[0]];
  return !!top && top.name == 'question' && interf.question_mode == REPLAY_OFFER_MODE;
}

/** Closes the offer's window (the popup's own slide out) and updates the button under it. */
function closeOffer(interf: any): void {
  syncReplayButton(interf.FWP);
  if (isOfferOpen(interf)) interf.remove();
}

/** "Accept": the replay is saved once; the battle is 'accepted' when the server answered. */
export function acceptReplayOffer(interf: any): void {
  const fwp = interf.FWP;
  const o = replayOffer(fwp);
  if (!o) {
    closeOffer(interf);
    return;
  }
  if (fwp.net_pending || fwp.animation) return;
  fwp.save_replay(o, (refused: boolean) => {
    decide(fwp, o.key, refused ? 'declined' : 'accepted');
    closeOffer(interf);
    fwp.net_notice(refused ? MSG_REPLAY_REFUSED : MSG_REPLAY_SAVED, 4000);
  });
}

/** "Decline": the battle is never offered again. */
export function declineReplayOffer(interf: any): void {
  const fwp = interf.FWP;
  if (fwp.net_pending || fwp.animation) return;
  const o = replayOffer(fwp);
  if (o) decide(fwp, o.key, 'declined');
  closeOffer(interf);
}

/** replay.save's refusal (VALIDATION_FAILED: the string's checksum) is an answer, not a failure to retry. */
export function saveRefused(error: unknown): boolean {
  return netErrorCode(error) == 'VALIDATION_FAILED';
}

/** The offer's window (new_interf.render of menu_question in REPLAY_OFFER_MODE; every render, idempotent). */
export function renderReplayOffer(interf: any, link: any): void {
  const fwp = interf.FWP;
  link.header.text = MSG_REPLAY_OFFER_TITLE;
  if ((link.main as any).$offerText !== MSG_REPLAY_OFFER_TEXT) {
    link.main.text = MSG_REPLAY_OFFER_TEXT;
    (link.main as any).$offerText = MSG_REPLAY_OFFER_TEXT;
    shrinkToFit(link.main, 10);
  }
  // menu_question's "Continue": its button, its plate and its caption go (the two buttons take the row).
  const proceed = link.proceed;
  if (proceed) {
    proceed.visible = false;
    const p = proceed.getBounds(link);
    for (let n = 0; n < link.numChildren; n++) {
      const c = link.getChildAt(n);
      if (c === proceed || c.name === ACCEPT_NAME || c.name === DECLINE_NAME || !/^instance\d+$/.test(String(c.name))) continue;
      const b = c.getBounds(link);
      if (b.x >= p.x - 2 && b.y >= p.y - 2 && b.x + b.width <= p.x + p.width + 2 && b.y + b.height <= p.y + p.height + 2) c.visible = false;
    }
  }
  let accept = link.getChildByName(ACCEPT_NAME) as BigButton | null;
  let decline = link.getChildByName(DECLINE_NAME) as BigButton | null;
  if (!accept) {
    accept = new BigButton(fwp, ACCEPT_NAME, MSG_REPLAY_OFFER_ACCEPT, 'green');
    accept.addEventListener(MouseEvent.CLICK, () => acceptReplayOffer(interf));
    link.addChild(accept);
  }
  if (!decline) {
    decline = new BigButton(fwp, DECLINE_NAME, MSG_REPLAY_OFFER_DECLINE, 'beige');
    decline.addEventListener(MouseEvent.CLICK, () => declineReplayOffer(interf));
    link.addChild(decline);
  }
  const k = touchTextsOn() ? TOUCH_SCALE : 1;
  for (const b of [accept, decline]) {
    b.scaleX = b.scaleY = k;
    // (the art's own height: the green 43 px, the beige 40 px; one middle for both)
    if ((b as any).$artH === undefined) (b as any).$artH = b.button.getBounds(b).height;
    b.y = Math.round(ROW_Y + ((43 - (b as any).$artH) * k) / 2 - (k - 1) * 20);
  }
  const w = 145 * k;
  accept.x = Math.round(ROW_MID - GAP / 2 - w);
  decline.x = Math.round(ROW_MID + GAP / 2);
}
